import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import {
  BsBarChartFill,
  BsClockHistory,
  BsFingerprint,
  BsCpu,
  BsCalendar2Week,
  BsGraphUpArrow,
  BsLightningCharge,
  BsAwardFill,
} from 'react-icons/bs';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const cardTitles = [
  'Total FTE Worked',
  'Expected Hours',
  'Biometric Hours',
  'EPR Hours',
  'Worked Days',
  'Bio Productivity',
  'EPR Productivity',
  'Quality %',
];

const cardKeys = [
  'TotalFTEWorked',
  'ExpectedHours',
  'BiometricHours',
  'EPRHours',
  'Workeddays',
  'Bioproductivity',
  'Eprproductivity',
  'qualityPercentage',
];

const icons = [
  BsBarChartFill,
  BsClockHistory,
  BsFingerprint,
  BsCpu,
  BsCalendar2Week,
  BsGraphUpArrow,
  BsLightningCharge,
  BsAwardFill,
];

const springEffect = {
  type: 'spring',
  stiffness: 120,
  damping: 18,
};

const TopEfficiency = ({ filters }) => {
  const { accessToken } = useAuth();
  const [cardData, setCardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [countKey, setCountKey] = useState(0);

  useEffect(() => {
    const fetchCardStats = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const params = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) params[key] = filters[key];
        });

        const response = await api.get(`${baseUrl}/superadminDashboard/getDataFor17Cards`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });

        if (response.data?.success) {
          setCardData(response.data.data || {});
          setCountKey((prev) => prev + 1);
        } else {
          setCardData({});
        }
      } catch (error) {
        console.error('Error fetching Top Efficiency card stats:', error);
        setCardData({});
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchCardStats();
  }, [filters, accessToken]);

  const isDataEmpty = !loading && Object.values(cardData).every((val) => val == null || val === '');

  const NoDataDisplay = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center animate-fadeIn">
      <div className="relative h-16 w-16 mb-6">
        <div className="absolute inset-0 rounded-full border-4 animate-spin"
          style={{
            borderTopColor: '#EE1D23',
            borderRightColor: '#1A2B5C',
            borderBottomColor: 'transparent',
            borderLeftColor: 'transparent',
            borderStyle: 'solid',
          }}
        />
        <div className="absolute inset-2 rounded-full bg-white" />
      </div>
      <h2 className="text-xl font-semibold text-[#1A2B5C] mb-2">No Efficiency Data Found</h2>
      <p className="text-sm text-gray-500 max-w-xs">
        Please check your filters or try again later. No valid metrics were received from the server.
      </p>
    </div>
  );

  return (
    <div className="px-6 py-10">
      <style>
        {`
        @keyframes fadeIn {
          0% { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out both;
        }
        `}
      </style>

      {isDataEmpty ? (
        <NoDataDisplay />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {cardTitles.map((title, index) => {
            const Icon = icons[index];
            const key = cardKeys[index];
            const value = cardData?.[key];
            const isNumeric = !isNaN(Number(value));
            const numericValue = parseFloat(value);

            const baseColor = index % 2 === 0 ? '#EE1D23' : '#1A2B5C';
            // const iconColor = index % 2 === 0 ? '#1A2B5C' : '#EE1D23';

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08, ...springEffect }}
              className="relative overflow-hidden bg-[#1a2b5cf2] text-white rounded-2xl p-6 shadow-xl backdrop-blur-md border border-white/10 transform transition-transform duration-500 ease-out will-change-transform hover:scale-105 hover:shadow-[0_8px_32px_rgba(238,29,35,0.4)] hover:ring-2 hover:ring-[#EE1D23]/30"

              >
                {/* Glow Rings */}
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#EE1D23]/20 rounded-full blur-[60px] z-0 animate-pulse" />
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#1A2B5C]/20 rounded-full blur-[60px] z-0 animate-pulse" />

                <div className="absolute inset-0 z-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent" />

                <div className="relative z-10 flex flex-col items-center text-center space-y-2">
                  <div
                    className="p-3 rounded-full shadow-md text-white text-3xl transition-all duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${baseColor}, #ffffff22)`,
                      boxShadow: `0 0 15px ${baseColor}66`,
                    }}
                  >
                    <Icon />
                  </div>
                  <h3 className="text-sm font-semibold tracking-wider opacity-90">{title}</h3>
                  <p className="text-3xl font-bold">
                    {loading ? (
                      <span className="animate-pulse text-gray-300">--</span>
                    ) : isNumeric ? (
                      <CountUp key={countKey} end={numericValue} duration={2.2} separator="," />
                    ) : (
                      value ?? 'N/A'
                    )}
                  </p>
                  <div className="relative mt-2 h-1 w-16 bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] rounded-full">
                    <div className="absolute inset-0 bg-white opacity-20 blur-md rounded-full animate-ping" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopEfficiency;
